import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Check, Link2 } from 'lucide-react';
import { useApi } from '../api/useApi';
import type { ResearchDetail } from '../api/client';
import { useAssistant } from '../components/assistant/AssistantProvider';
import { askAlmanacAssistant } from '../lib/ask-assistant';
import { useCopyToClipboard } from '../lib/copy';
import { POSTER_SANDBOX, usePosterColorScheme, withPosterLinkTargets } from '../lib/poster';

/** Fetch state for the standalone poster route. */
type PosterState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; item: ResearchDetail };

/**
 * Full-viewport, chrome-free poster view. Renders ONLY the sandboxed poster in
 * a fixed `100dvh` iframe that scrolls natively inside itself (the "raw doc"
 * feel) — no height-sync, no injected overflow clipping, no measure script. A
 * single floating pill links back to the framed item page so the viewer is
 * never trapped. Serves both the "open raw in new tab" action and a shareable
 * full-screen URL.
 */
export function ResearchPosterPage() {
  const { researchId = '' } = useParams();
  const api = useApi();
  const assistant = useAssistant();
  const [state, setState] = useState<PosterState>({ status: 'loading' });
  const posterColorScheme = usePosterColorScheme();
  const { copiedKey, copyError, copy } = useCopyToClipboard();
  // Same reference the MCP `get_research` tool accepts — Almanac runs as an
  // installed PWA, so there is no URL bar to copy it from.
  const referenceUrl = `${window.location.origin}/research/${researchId}`;

  useEffect(() => {
    setState({ status: 'loading' });
    api
      .getResearch(researchId)
      .then((data) => setState({ status: 'loaded', item: data.item }))
      .catch((e: unknown) =>
        setState({ status: 'error', message: e instanceof Error ? e.message : String(e) }),
      );
  }, [api, researchId]);

  if (state.status === 'loading') {
    return (
      <div className="fixed inset-0 grid place-items-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="fixed inset-0 grid place-items-center px-4 text-center text-sm text-danger">
        {state.message}
      </div>
    );
  }

  // A poster route without poster HTML has nothing to show — send the viewer to
  // the full item page instead.
  if (!state.item.body_html) {
    return <Navigate to={`/research/${researchId}`} replace />;
  }

  return (
    <div
      className={
        assistant.open ? 'fixed inset-y-0 left-0 right-0 lg:right-[26rem]' : 'fixed inset-0'
      }
    >
      {/*
        SECURITY (load-bearing): the agent-authored poster runs in a sandboxed
        iframe WITHOUT allow-same-origin, giving it a unique opaque origin with
        no reach into the parent DOM, cookies, or OAuth tokens. NEVER add
        allow-same-origin. (See web/src/lib/poster.ts.)
      */}
      <iframe
        title={state.item.title}
        srcDoc={withPosterLinkTargets(state.item.body_html)}
        sandbox={POSTER_SANDBOX}
        className="block border-0"
        style={{ height: '100dvh', width: '100%', colorScheme: posterColorScheme }}
      />
      <Link
        to={`/research/${researchId}`}
        className="fixed left-4 top-4 z-10 rounded-full border border-border bg-background/80 px-3 py-1.5 text-sm backdrop-blur hover:bg-background"
      >
        ← {state.item.title}
      </Link>
      <div
        className={
          assistant.open
            ? 'fixed top-4 right-4 z-10 flex items-center gap-2 lg:right-[27rem]'
            : 'fixed top-4 right-4 z-10 flex items-center gap-2'
        }
      >
        <button
          type="button"
          onClick={() => copy('reference', referenceUrl)}
          title="Copy a link to this research — paste it to an agent to load it"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/80 px-3 py-1.5 text-sm backdrop-blur hover:bg-background"
        >
          {copiedKey === 'reference' ? <Check size={14} /> : <Link2 size={14} />}
          {copiedKey === 'reference' ? 'Copied' : 'Copy link'}
        </button>
        {copyError ? (
          <output className="rounded-full border border-danger/40 bg-background/80 px-3 py-1.5 text-sm text-danger backdrop-blur">
            {copyError}
          </output>
        ) : null}
        <button
          type="button"
          onClick={() => askAlmanacAssistant({ researchId })}
          className="rounded-full border border-primary/40 bg-background/80 px-3 py-1.5 text-sm text-primary backdrop-blur hover:bg-background"
        >
          Ask about this research
        </button>
      </div>
    </div>
  );
}
